/**
 * Digital SAT — Daily Challenge Widget (rewrite).
 *
 * Data model (from backend /api/exam/v2/daily-challenge):
 *   question.options = [{key: 'A', text: '...'}, ...]     for MCQ
 *   question.question_type = 'mcq' | 'spr'
 *
 * States
 *   - loading
 *   - ready               (options rendered)
 *   - selected            (an option is chosen, ready to submit)
 *   - submitting
 *   - correct / incorrect (feedback view)
 *   - already_completed   (logged-in, already submitted today)
 *   - unavailable         (backend has no eligible question)
 *   - error               (network hiccup)
 *
 * Anonymous rotation
 *   Recent question IDs are stored in localStorage under
 *   `dsat_dc_recent_ids` so anon visitors don't get the same question twice
 *   in a row. The list is capped at 30 entries.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, CheckCircle2, XCircle, Trophy, Loader2, ArrowRight, Lock, RefreshCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const SECTION_LABEL = { rw: 'Reading & Writing', math: 'Math' };
const LS_RECENT_KEY = 'dsat_dc_recent_ids';
const RECENT_CAP = 30;

function readRecent() {
  try {
    const raw = localStorage.getItem(LS_RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string').slice(-RECENT_CAP) : [];
  } catch { return []; }
}
function pushRecent(qid) {
  try {
    const list = readRecent().filter((x) => x !== qid);
    list.push(qid);
    while (list.length > RECENT_CAP) list.shift();
    localStorage.setItem(LS_RECENT_KEY, JSON.stringify(list));
  } catch { /* ignore */ }
}

export default function DSATDailyChallengeWidget() {
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);   // MCQ key ('A'/'B'/'C'/'D')
  const [sprValue, setSprValue] = useState('');
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startedAt, setStartedAt] = useState(Date.now());

  const isMCQ = data?.question?.question_type !== 'spr';
  const isReady = !!data?.ok && !data?.empty && Array.isArray(data?.question?.options);
  const options = useMemo(() => {
    const raw = data?.question?.options || [];
    // Normalise to [{key, text}] shape defensively
    return raw.map((o, i) => (typeof o === 'string'
      ? { key: String.fromCharCode(65 + i), text: o }
      : { key: (o?.key || String.fromCharCode(65 + i)), text: (o?.text ?? '') }));
  }, [data]);

  const canSubmit = isMCQ ? !!selectedKey : !!sprValue.trim();

  const load = async () => {
    setLoading(true); setResult(null); setSelectedKey(null); setSprValue(''); setError(null);
    try {
      const params = new URLSearchParams();
      const recent = readRecent();
      if (recent.length) params.set('recent_ids', recent.join(','));
      const [c, meR] = await Promise.all([
        ax.get(`/api/exam/v2/daily-challenge${params.toString() ? `?${params}` : ''}`),
        ax.get('/api/auth/me').catch(() => ({ data: null })),
      ]);
      setData(c.data);
      setMe(meR.data || null);
      setStartedAt(Date.now());
    } catch (_e) {
      setError('Could not load today\u2019s SAT challenge — please retry.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!canSubmit || !data?.question?.question_id) return;
    setSubmitting(true);
    const time_seconds = Math.max(1, Math.floor((Date.now() - startedAt) / 1000));
    try {
      const answer = isMCQ ? selectedKey : sprValue.trim();
      const r = await ax.post('/api/exam/v2/daily-challenge/submit', {
        question_id: data.question.question_id, answer, time_seconds,
      });
      setResult(r.data);
      // Record locally so we don't hit the same one again on refresh (esp. anon)
      pushRecent(data.question.question_id);
      if (r.data?.xp_awarded > 0) toast.success(`+${r.data.xp_awarded} XP earned!`);
      (r.data?.new_achievements || []).forEach((a) => toast.success(`Unlocked: ${a.name}`));
    } catch (_e) { setError('Submission failed — please retry.'); }
    finally { setSubmitting(false); }
  };

  const tryAnother = () => load();

  // -------- Render states --------

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-sm text-white/70" data-testid="dsat-dc-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading today&apos;s SAT challenge…
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <div className="text-sm text-rose-200" data-testid="dsat-dc-error">{error}</div>
        <button onClick={load} className="mt-3 text-[11px] uppercase tracking-wider text-[#D3D86F] hover:underline inline-flex items-center gap-1"
          data-testid="dsat-dc-retry">
          <RefreshCcw className="h-3 w-3" /> Retry
        </button>
      </Shell>
    );
  }

  if (data?.enabled === false) {
    return (
      <Shell>
        <div className="text-sm text-white/70" data-testid="dsat-dc-disabled">
          The Digital SAT Daily Challenge is paused. Come back soon.
        </div>
      </Shell>
    );
  }

  if (data?.empty || !isReady) {
    return (
      <Shell>
        <div className="text-sm text-white/80" data-testid="dsat-dc-unavailable">
          No eligible Digital SAT question is available right now — our team is
          reviewing new items. Come back tomorrow or explore the practice sets below.
        </div>
      </Shell>
    );
  }

  const q = data.question;
  const correctLetter = typeof result?.correct_answer === 'string'
    ? result.correct_answer.trim().toUpperCase() : null;

  return (
    <Shell testid="dsat-daily-challenge-widget"
      section={data.section} topic={data.topic_title} q={q}
      xp={data.xp_reward}>
      {/* Passage — optional */}
      {q.passage && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white/85 mb-2 max-h-52 overflow-y-auto"
             data-testid="dsat-dc-passage">
          {q.passage}
        </div>
      )}

      {/* Prompt */}
      <p className="text-sm sm:text-base text-white/90 leading-relaxed whitespace-pre-line bg-white/5 border border-white/10 rounded-xl p-3"
         data-testid="dsat-dc-prompt">{q.prompt}</p>

      {/* MCQ options */}
      {isMCQ && (
        <div className="space-y-2 mt-3" data-testid="dsat-dc-choices" role="radiogroup" aria-label="Answer choices">
          {options.map((opt) => {
            const isSel = selectedKey === opt.key;
            const isCorrect = result && correctLetter === opt.key;
            const isWrong = result && isSel && correctLetter !== opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => !result && setSelectedKey(opt.key)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && !result) {
                    e.preventDefault(); setSelectedKey(opt.key);
                  }
                }}
                disabled={!!result}
                role="radio"
                aria-checked={isSel}
                data-testid={`dsat-dc-choice-${opt.key}`}
                className={`w-full text-left px-3 py-3 rounded-xl border transition flex items-start gap-3 text-sm min-h-[48px] ${
                  isCorrect ? 'bg-emerald-500/15 border-emerald-400 text-white'
                  : isWrong ? 'bg-rose-500/15 border-rose-400 text-white'
                  : isSel ? 'bg-[#D3D86F]/15 border-[#D3D86F] text-white ring-2 ring-[#D3D86F]/40'
                  : 'bg-white/5 border-white/10 hover:border-[#D3D86F]/40 text-white/85'
                }`}>
                <span className={`text-[10px] uppercase tracking-wider mt-1 shrink-0 font-mono min-w-[16px] ${
                  isSel || isCorrect ? 'text-white' : 'text-white/50'
                }`}>{opt.key}</span>
                <span className="flex-1">{opt.text}</span>
                {isCorrect && <CheckCircle2 className="h-4 w-4 text-emerald-300 shrink-0" />}
                {isWrong && <XCircle className="h-4 w-4 text-rose-300 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}

      {/* SPR numeric input */}
      {!isMCQ && (
        <div className="mt-3" data-testid="dsat-dc-spr-wrapper">
          <label htmlFor="dsat-dc-spr" className="text-[10px] uppercase tracking-wider text-white/60 block mb-1">
            Your answer (integer, decimal or fraction — e.g. 3.14 or 3/8)
          </label>
          <input
            id="dsat-dc-spr"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={sprValue}
            onChange={(e) => setSprValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit && !result) submit(); }}
            disabled={!!result}
            placeholder="e.g. 3.14 or 3/8"
            className="w-full bg-white/5 border border-white/20 rounded-xl px-3 py-3 text-sm text-white placeholder-white/40 focus:outline-none focus:border-[#D3D86F] disabled:opacity-60"
            data-testid="dsat-dc-spr"
          />
        </div>
      )}

      {/* Submit / feedback */}
      {!result ? (
        <button
          onClick={submit}
          disabled={!canSubmit || submitting}
          className="mt-4 w-full bg-[#D3D86F] hover:bg-[#c4ca5e] disabled:opacity-40 disabled:cursor-not-allowed text-black py-3 rounded-full font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2"
          data-testid="dsat-dc-submit"
        >
          {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trophy className="h-3.5 w-3.5" />}
          {submitting ? 'Submitting…' : 'Submit answer'}
        </button>
      ) : (
        <FeedbackBlock result={result} me={me} onAnother={tryAnother}
          alreadyCompleted={data.already_completed && !result.xp_awarded && !!me} />
      )}
    </Shell>
  );
}


function Shell({ children, testid, section, topic, q, xp }) {
  return (
    <section
      className="rounded-2xl border border-[#D3D86F]/40 bg-gradient-to-br from-black via-[#0a0a0a] to-black text-white p-5 sm:p-6 shadow-lg relative overflow-hidden"
      data-testid={testid || 'dsat-daily-challenge-widget'}
    >
      <div className="absolute inset-0 opacity-50 pointer-events-none" style={{
        background: 'radial-gradient(60% 50% at 0% 0%, rgba(220,38,38,0.30) 0%, rgba(220,38,38,0) 60%), radial-gradient(50% 40% at 100% 100%, rgba(211,216,111,0.18) 0%, rgba(211,216,111,0) 70%)',
      }} />
      <div className="relative">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D3D86F] flex items-center gap-1.5">
              <Trophy className="h-3 w-3" /> Today&apos;s Digital SAT Challenge
            </div>
            {q ? (
              <>
                <h3 className="font-bold text-base sm:text-lg mt-0.5" data-testid="dsat-dc-title">
                  {SECTION_LABEL[section] || SECTION_LABEL[q.section] || 'Digital SAT'} — {topic || q.skill || 'Skill of the Day'}
                </h3>
                <div className="text-[11px] text-white/65 mt-0.5">
                  {(q.difficulty || 'Mixed')} · {Math.max(1, Math.round((q.estimated_seconds || 90) / 60))} min · Reward +{xp || 5} XP
                </div>
              </>
            ) : (
              <h3 className="font-bold text-base sm:text-lg mt-0.5">Daily Digital SAT Challenge</h3>
            )}
          </div>
          {xp && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-white/50">Reward</div>
              <div className="text-base font-bold text-[#D3D86F] flex items-center gap-1 justify-end">
                <Sparkles className="h-3.5 w-3.5" />+{xp} XP
              </div>
            </div>
          )}
        </div>
        {children}
      </div>
    </section>
  );
}


function FeedbackBlock({ result, me, onAnother, alreadyCompleted }) {
  return (
    <div className="mt-4 space-y-3" data-testid="dsat-dc-result">
      <div className={`rounded-xl px-3 py-2 text-sm font-bold ${result.is_correct
        ? 'bg-emerald-500/15 text-emerald-200 border border-emerald-400/40'
        : 'bg-rose-500/15 text-rose-200 border border-rose-400/40'}`}
        data-testid={`dsat-dc-verdict-${result.is_correct ? 'correct' : 'incorrect'}`}>
        {result.is_correct
          ? 'Correct — nicely done.'
          : <>Not quite. Correct answer: <b>{result.correct_answer}</b>.</>}
        {result.xp_awarded > 0 && <span className="ml-2 text-[#D3D86F]">· +{result.xp_awarded} XP earned</span>}
      </div>
      {result.explanation && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white/85" data-testid="dsat-dc-explanation">
          <div className="text-[10px] uppercase tracking-wider text-[#D3D86F] font-bold mb-1">Explanation</div>
          {result.explanation}
        </div>
      )}
      {result.trap_explanation && (
        <div className="bg-amber-500/10 border border-amber-400/30 rounded-xl p-3 text-sm text-amber-100" data-testid="dsat-dc-trap">
          <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold mb-1">Why students miss it</div>
          {result.trap_explanation}
        </div>
      )}
      {result.skill_to_revise && (
        <div className="text-[11px] text-white/70" data-testid="dsat-dc-skill">
          Skill: <b className="text-white">{result.skill_to_revise}</b>
          {result.similar_practice_link && (
            <> · <Link to={result.similar_practice_link} className="underline text-[#D3D86F]" data-testid="dsat-dc-similar">Try similar practice</Link></>
          )}
        </div>
      )}
      {!me && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white/85" data-testid="dsat-dc-cta-anon">
          <div className="font-bold mb-1.5 flex items-center gap-1.5">
            <Lock className="h-3.5 w-3.5" /> Sign in to save your XP and streak.
          </div>
          <div className="text-[11px] text-white/60">Create a free student account to bank your progress and unlock personalised recommendations.</div>
          <div className="flex flex-wrap gap-2 mt-2">
            <Link to="/student/signup?program=digital-sat" data-testid="dsat-dc-cta-signup"
              className="text-[11px] bg-[#D3D86F] hover:bg-[#c4ca5e] text-black px-4 py-2 rounded-full font-bold uppercase tracking-wider inline-flex items-center gap-1">
              Create student account <ArrowRight className="h-3 w-3" />
            </Link>
            <Link to="/student/login" data-testid="dsat-dc-cta-login"
              className="text-[11px] bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full font-bold uppercase tracking-wider inline-flex items-center gap-1">
              Student login
            </Link>
          </div>
        </div>
      )}
      {me && alreadyCompleted && (
        <div className="text-[11px] text-white/65 italic" data-testid="dsat-dc-already-done">
          <Lock className="inline h-3 w-3 mr-1" /> You&apos;ve already claimed today&apos;s XP — come back tomorrow for a fresh challenge.
        </div>
      )}
      {onAnother && (
        <button onClick={onAnother}
          className="text-[11px] uppercase tracking-wider text-white/70 hover:text-white inline-flex items-center gap-1"
          data-testid="dsat-dc-another">
          <RefreshCcw className="h-3 w-3" /> Try another
        </button>
      )}
    </div>
  );
}
